import { useState } from "react";
import { ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fmtDate } from "@/lib/api";
import {
  useApi, post, put, Panel, PageHead, Stat, QuickForm, DataTable, StatusBadge, Tabs,
} from "@/crm/ui";

const TABS = ["Providers", "API Integrations", "Consent Management", "API Audit", "API Usage"];

export default function Integrations() {
  const [tab, setTab] = useState("Providers");
  const [form, setForm] = useState({ environment: "sandbox", status: "Not Configured" });
  const [consentForm, setConsentForm] = useState({ consent_type: "Credit Bureau" });
  const catalog = useApi("/crm/integrations/catalog");
  const rows = useApi("/crm/integrations");
  const consents = useApi("/crm/consents");
  const leads = useApi("/crm/leads");
  const apiAudit = useApi(tab === "API Audit" ? "/crm/api-audit" : null, [tab]);
  const usage = useApi(tab === "API Usage" ? "/crm/api-usage" : null, [tab]);
  const list = catalog.data?.integrations || [];

  return (
    <div className="space-y-6" data-testid="integrations-page">
      <PageHead eyebrow="Integrations" title="External data providers"
                subtitle="Provider-independent architecture for credit bureau, GST, KYC, ITR, account aggregator and e-sign. Credentials live only in server environment variables and are never returned to the browser." />

      <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4"
           data-testid="not-configured-banner">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
        <p className="text-xs text-amber-900">
          No external provider is configured yet, so bureau / GST / KYC checks are intentionally
          blocked and return a clear error. <b>No sample or simulated bureau data is ever shown.</b>
          {" "}Loan approval or rejection is never automated from these analyses.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="Provider types" value={list.length} testid="int-types" />
        <Stat label="Configured" value={list.filter((i) => i.state === "Configured").length} testid="int-configured" />
        <Stat label="Consents captured" value={(consents.data || []).length} testid="int-consents" />
        <Stat label="API calls logged" value={usage.data?.summary?.calls ?? (apiAudit.data?.total ?? "—")} testid="int-calls" />
      </div>

      <Tabs tabs={TABS} active={tab} onChange={setTab} testid="integration-tabs" />

      {tab === "Providers" && (
        <Panel title="Provider catalog & configuration status" testid="provider-catalog-panel">
          <DataTable testid="provider-catalog-table" rows={list}
                     columns={[{ key: "label", label: "Integration" },
                       { key: "integration_type", label: "Type" },
                       { key: "provider", label: "Selected Provider" },
                       { key: "providers", label: "Supported Providers", render: (r) => (r.providers || []).join(", ") },
                       { key: "consent_type", label: "Consent Required" },
                       { key: "credential_env_var", label: "Credential Env Var", render: (r) => r.credential_env_var || "—" },
                       { key: "credentials_present", label: "Credentials", render: (r) => <StatusBadge value={r.credentials_present ? "Present" : "Missing"} /> },
                       { key: "provider_selection", label: "Provider Selection", render: (r) => <StatusBadge value={r.provider_selection || "Pending"} /> },
                       { key: "state", label: "State", render: (r) => <StatusBadge value={r.state} /> }]} />
          <p className="mt-3 text-xs text-muted-foreground" data-testid="provider-pending-note">
            CIBIL / Credit Bureau, GST and KYC providers are <strong>pending provider selection</strong> —
            no provider is wired, no external call is made and no sample bureau data is ever returned
            until real credentials are configured here.
          </p>
          <p className="mt-3 text-xs text-muted-foreground">
            Metrics captured once configured: {list.map((i) => `${i.label}: ${(i.metrics || []).slice(0, 4).join(", ")}`).join(" · ")}
          </p>
        </Panel>
      )}

      {tab === "API Integrations" && (
        <>
          <Panel title="Configure an integration" testid="integration-form-panel"
                 aside={<span className="text-xs text-muted-foreground">Secrets are referenced by env-var name only</span>}>
            <QuickForm testid="integration-form" values={form} submitLabel="Save integration"
                       onChange={(k, v) => setForm((f) => ({ ...f, [k]: v }))}
                       fields={[
                         { key: "integration_type", label: "Integration Type", options: list.map((i) => ({ value: i.integration_type, label: i.label })) },
                         { key: "provider", label: "Provider" },
                         { key: "environment", label: "Environment", options: ["sandbox", "uat", "production"] },
                         { key: "base_url", label: "Base URL" },
                         { key: "api_version", label: "API Version" },
                         { key: "auth_type", label: "Authentication Type", options: ["api_key", "oauth2", "basic", "jwt", "mtls"] },
                         { key: "credential_env_var", label: "Credential Env Var Name" },
                         { key: "timeout", label: "Timeout (seconds)", type: "number" },
                         { key: "retry_count", label: "Retry Count", type: "number" },
                         { key: "status", label: "Status", options: ["Not Configured", "Active", "Inactive"] },
                       ]}
                       onSubmit={async () => {
                         const r = await post("/crm/integrations", form, "Integration saved");
                         if (r) { rows.reload(); catalog.reload(); }
                       }} />
          </Panel>
          <Panel title={`Integration master (${(rows.data || []).length})`} testid="integration-master-panel">
            <DataTable testid="integration-master-table" rows={rows.data}
                       empty="No integrations configured yet."
                       columns={[{ key: "integration_id", label: "Integration ID" },
                         { key: "provider", label: "Provider" },
                         { key: "integration_type", label: "Type" },
                         { key: "environment", label: "Environment" },
                         { key: "base_url", label: "Base URL" },
                         { key: "api_version", label: "Version" },
                         { key: "auth_type", label: "Auth" },
                         { key: "credential_env_var", label: "Credential Env Var" },
                         { key: "timeout", label: "Timeout", right: true },
                         { key: "retry_count", label: "Retries", right: true },
                         { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> }]} />
          </Panel>
        </>
      )}

      {tab === "Consent Management" && (
        <>
          <Panel title="Capture consent" testid="consent-form-panel">
            <QuickForm testid="consent-form" values={consentForm} submitLabel="Capture consent"
                       onChange={(k, v) => setConsentForm((f) => ({ ...f, [k]: v }))}
                       fields={[
                         { key: "lead_id", label: "Lead", options: (leads.data || []).map((l) => ({ value: l.lead_id, label: `${l.customer_name} (${l.lead_id})` })) },
                         { key: "consent_type", label: "Consent Type", options: ["Credit Bureau", "GST", "KYC", "Banking Data", "Other"] },
                         { key: "provider", label: "Provider" },
                         { key: "purpose", label: "Purpose" },
                         { key: "expiry_date", label: "Expiry Date", type: "date" },
                         { key: "reference", label: "Consent Reference" },
                       ]}
                       onSubmit={async () => {
                         const lead = (leads.data || []).find((l) => l.lead_id === consentForm.lead_id);
                         const r = await post("/crm/consents",
                           { ...consentForm, customer_name: lead?.customer_name, status: "Active" },
                           "Consent captured");
                         if (r) { setConsentForm({ consent_type: "Credit Bureau" }); consents.reload(); }
                       }} />
          </Panel>
          <Panel title={`Consents (${(consents.data || []).length})`} testid="consents-panel">
            <DataTable testid="consents-table" rows={consents.data} empty="No consents captured yet."
                       columns={[{ key: "consent_id", label: "Consent ID" },
                         { key: "lead_id", label: "Lead" }, { key: "customer_name", label: "Customer" },
                         { key: "consent_type", label: "Type" }, { key: "provider", label: "Provider" },
                         { key: "purpose", label: "Purpose" },
                         { key: "consent_date", label: "Captured", render: (r) => fmtDate(r.consent_date) },
                         { key: "expiry_date", label: "Expiry", render: (r) => r.expiry_date || "—" },
                         { key: "captured_by", label: "Captured By" },
                         { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
                         { key: "actions", label: "Actions", render: (r) => (
                           <span className="flex gap-1">
                             {["Active", "Revoked", "Expired"].filter((s) => s !== r.status).map((s) => (
                               <Button key={s} size="sm" variant="outline" className="bg-white text-[10px]"
                                       data-testid={`consent-${s.toLowerCase()}-${r.consent_id}`}
                                       onClick={async () => {
                                         await put(`/crm/consents/${r.consent_id}`,
                                           { status: s, reason: `Marked ${s} by operator` }, `Consent ${s}`);
                                         consents.reload();
                                       }}>{s}</Button>))}
                           </span>) }]} />
          </Panel>
        </>
      )}

      {tab === "API Audit" && (
        <Panel title={`API audit (${apiAudit.data?.total ?? 0})`} testid="api-audit-panel"
               aside={<span className="text-xs text-muted-foreground">Metadata only — raw provider responses are never stored here</span>}>
          <DataTable testid="api-audit-table" rows={apiAudit.data?.records}
                     empty="No external API calls have been attempted yet."
                     columns={[{ key: "created_at", label: "Date / Time", render: (r) => (r.created_at || "").replace("T", " ").slice(0, 19) },
                       { key: "integration_type", label: "Integration" },
                       { key: "provider", label: "Provider" }, { key: "lead_id", label: "Lead" },
                       { key: "user_email", label: "User" }, { key: "request_type", label: "Request" },
                       { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
                       { key: "response_status", label: "Response" },
                       { key: "error_code", label: "Error Code" },
                       { key: "processing_ms", label: "Time (ms)", right: true },
                       { key: "reference_id", label: "Reference" }]} />
        </Panel>
      )}

      {tab === "API Usage" && (
        <Panel title="API usage report" testid="api-usage-panel"
               aside={usage.data && <span className="text-xs text-muted-foreground">
                 {usage.data.summary.calls} calls · {usage.data.summary.success} success ·
                 {" "}{usage.data.summary.blocked} blocked
               </span>}>
          <DataTable testid="api-usage-table" rows={usage.data?.rows} empty="No API usage recorded."
                     columns={[{ key: "integration_type", label: "Integration" },
                       { key: "provider", label: "Provider" },
                       { key: "calls", label: "Calls", right: true },
                       { key: "success", label: "Success", right: true },
                       { key: "blocked", label: "Blocked", right: true },
                       { key: "failed", label: "Failed", right: true },
                       { key: "avg_ms", label: "Avg ms", right: true },
                       { key: "last_call", label: "Last Call", render: (r) => (r.last_call || "").replace("T", " ").slice(0, 19) }]} />
        </Panel>
      )}
    </div>
  );
}
